import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  ShoppingBag, Timer, Ban, CheckCircle2, RefreshCw, Truck,
  Wallet, TrendingUp, Percent, Link2, Boxes, ArrowRightCircle, AlertTriangle, MapPin, RotateCcw, Undo2, Upload, EyeOff,
  type LucideIcon,
} from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { formatINR } from '@/lib/format';
import {
  getShopifyOrders, getShopifySkuMap, getShopifyStockPreview,
  shopifyOrderBucket as bucketOf, type ShopifyOrderBucket,
} from '@/api/shopify';
import { getOnlineProfitability } from '@/api/onlineProfitability';
import { listShippingRows, type Shipment } from '@/api/delhivery';
import { PeriodPicker, REPORT_UNITS, usePeriod, parseYmd, ymd, type PeriodRange } from '@/components/ui/PeriodPicker';

const money = (n: number) => `₹${formatINR(n)}`;
/** Compact rupees for tiles and axis ticks (₹9.29L); the full value stays in tooltips. */
const compact = (n: number) =>
  Math.abs(n) >= 1_00_00_000 ? `₹${(n / 1_00_00_000).toFixed(2)}Cr`
  : Math.abs(n) >= 1_00_000 ? `₹${(n / 1_00_000).toFixed(2)}L`
  : Math.abs(n) >= 1_000 ? `₹${(n / 1_000).toFixed(1)}K`
  : `₹${Math.round(n)}`;
const axisMoney = (n: number) => compact(n).replace(/\.0+(?=[A-Za-z])/, '');
const pct = (v: number, total: number) => (total > 0 && v > 0 ? `${((v / total) * 100).toFixed(v / total < 0.1 ? 1 : 0)}%` : '0%');

// ERP theme colours (same set as the home dashboard) — each entity keeps its colour on every chart and tile.
const C = { accent: '#FF8C00', blue: '#3B82F6', green: '#10B981', violet: '#8B5CF6', amber: '#F59E0B', red: '#EF4444', gray: '#94A3B8' };
const SURFACE = 'var(--bg-secondary)';
const AXIS_TICK = { fontSize: 10, fill: 'var(--text-muted)' };
const GRID = { strokeDasharray: '3 3', stroke: 'rgba(0,0,0,0.18)', vertical: false } as const;
const TREND_DAYS = 30;

const ORDER_SEG: { key: ShopifyOrderBucket; label: string; color: string }[] = [
  { key: 'received', label: 'Awaiting', color: C.blue },
  { key: 'processing', label: 'Processing', color: C.accent },
  { key: 'done', label: 'Done', color: C.green },
  { key: 'held', label: 'On Hold', color: C.violet },
  { key: 'cancelled', label: 'Cancelled', color: C.red },
  { key: 'ignored', label: 'Ignored', color: C.gray },
];

/** Map a Delhivery shipment status onto a delivery funnel stage. */
const SHIP_STAGE: Record<string, 'booked' | 'transit' | 'delivered' | 'ndr' | 'rto' | 'void'> = {
  Booked: 'booked', Manifested: 'booked', PickupRequested: 'booked', PickedUp: 'booked',
  InTransit: 'transit', OutForDelivery: 'transit',
  Delivered: 'delivered',
  NDR: 'ndr', RTO: 'rto', Failed: 'void', Cancelled: 'void',
};
const SHIP_SEG: { key: 'notBooked' | 'booked' | 'transit' | 'delivered' | 'ndr' | 'rto' | 'void'; label: string; color: string }[] = [
  { key: 'notBooked', label: 'Not booked', color: C.gray },
  { key: 'booked', label: 'Booked', color: C.blue },
  { key: 'transit', label: 'In transit', color: C.accent },
  { key: 'delivered', label: 'Delivered', color: C.green },
  { key: 'ndr', label: 'NDR (delivery failed)', color: C.amber },
  { key: 'rto', label: 'RTO', color: C.red },
  { key: 'void', label: 'Cancelled / failed', color: C.violet },
];

type RtoStage = 'returning' | 'returned' | 'ndr';
const RTO_STAGE: Record<RtoStage, { label: string; color: string }> = {
  returning: { label: 'Returning to origin', color: C.red },
  returned: { label: 'Returned to origin', color: C.violet },
  ndr: { label: 'NDR — at risk of RTO', color: C.amber },
};
/** Where a shipment sits on the return path, or null if it isn't on it. */
function rtoStage(s: Shipment): RtoStage | null {
  if (s.status === 'NDR') return 'ndr';
  if (s.status !== 'RTO') return null;
  // Delhivery reports a completed return as Status "RTO" (StatusType DL); on the way
  // back the status text is the leg's own ("In Transit", "Pending", …).
  if (/^rto$|returned|rto.?delivered/i.test(s.statusDetail ?? '')) return 'returned';
  try {
    if (JSON.parse(s.trackingJson ?? '[]')[0]?.type === 'DL') return 'returned';
  } catch { /* trackingJson is clipped on long journeys */ }
  return 'returning';
}

const PAY_SEG: { key: 'paid' | 'partial' | 'pending' | 'other'; label: string; color: string }[] = [
  { key: 'paid', label: 'Paid', color: C.green },
  { key: 'partial', label: 'Partially paid', color: C.amber },
  { key: 'pending', label: 'Pending', color: C.blue },
  { key: 'other', label: 'Voided / other', color: C.gray },
];
const PAY_BUCKET: Record<string, 'paid' | 'partial' | 'pending'> = { PAID: 'paid', PARTIALLY_PAID: 'partial', PENDING: 'pending' };

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
type Step = 'hour' | 'day' | 'week' | 'month';
type Bucket = { key: string; label: string; orders: number; costs: number; profit: number };
/** Chart buckets for the period and a function that places a timestamp in its bucket ('' = outside).
 *  A quarter goes by week and a year by month; otherwise the window's length decides — one day by hour,
 *  up to two months by day, up to ~six months by week, longer by month. No period = the last 30 days. */
function trendBuckets(range: PeriodRange | null): { step: Step; rows: Bucket[]; keyOf: (d: Date) => string } {
  const blank = (key: string, label: string): Bucket => ({ key, label, orders: 0, costs: 0, profit: 0 });
  const dayLabel = (d: Date) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const DAY = 86_400_000;
  const now = new Date(), today = ymd(now);
  let from: string, to: string;
  if (!range) { to = today; from = ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - (TREND_DAYS - 1))); }
  else {
    // A date range's open ends stop at today and two years back, so the chart stays readable.
    to = range.unit === 'custom' && range.to > today ? today : range.to;
    const end = parseYmd(to), floor = ymd(new Date(end.getFullYear() - 2, end.getMonth(), end.getDate()));
    from = range.from < floor ? floor : range.from;
  }
  const a = parseYmd(from), b = parseYmd(to), span = Math.round((b.getTime() - a.getTime()) / DAY) + 1;
  const step: Step = range?.unit === 'year' ? 'month' : range?.unit === 'quarter' ? 'week'
    : span <= 1 ? 'hour' : span <= 62 ? 'day' : span <= 190 ? 'week' : 'month';
  if (step === 'hour') {
    return { step, rows: Array.from({ length: 24 }, (_, h) => blank(String(h), `${String(h).padStart(2, '0')}:00`)), keyOf: (d) => (ymd(d) === from ? String(d.getHours()) : '') };
  }
  if (step === 'day') {
    const rows = Array.from({ length: span }, (_, i) => { const d = new Date(a.getFullYear(), a.getMonth(), a.getDate() + i); return blank(dayKey(d), dayLabel(d)); });
    return { step, rows, keyOf: dayKey };
  }
  if (step === 'week') {
    const start = a.getTime(), end = b.getTime(), WEEK = 7 * DAY, rows: Bucket[] = [];
    for (let t = start, i = 0; t <= end; t += WEEK, i++) rows.push(blank(`w${i}`, dayLabel(new Date(t))));
    return { step, rows, keyOf: (d) => { const day = parseYmd(ymd(d)).getTime(); return day < start || day > end ? '' : `w${Math.floor((day - start) / WEEK)}`; } };
  }
  const rows: Bucket[] = [], multiYear = a.getFullYear() !== b.getFullYear();
  for (let y = a.getFullYear(), m = a.getMonth(); y < b.getFullYear() || (y === b.getFullYear() && m <= b.getMonth()); m === 11 ? (y++, m = 0) : m++) {
    rows.push(blank(`${y}-${m}`, multiYear ? `${MON[m]} ${String(y).slice(2)}` : MON[m]));
  }
  return { step, rows, keyOf: (d) => `${d.getFullYear()}-${d.getMonth()}` };
}
const STEP_TEXT: Record<Step, { orders: string; value: string }> = {
  hour: { orders: 'Orders by hour', value: 'Hourly order value (ex-GST)' },
  day: { orders: 'Orders per day', value: 'Daily order value (ex-GST)' },
  week: { orders: 'Orders per week', value: 'Weekly order value (ex-GST)' },
  month: { orders: 'Orders per month', value: 'Monthly order value (ex-GST)' },
};

export function EcommerceDashboardPage() {
  const [hoverSeg, setHoverSeg] = useState<number | null>(null);
  // Orders, money and returns follow the period: orders and profit by order date, shipping by dispatch date.
  const period = usePeriod();
  const { range } = period;
  const win = { from: range?.from, to: range?.to };
  const ordersQ = useQuery({ queryKey: ['shopify-orders', { status: 'all', search: '', ...win }], queryFn: () => getShopifyOrders({ status: 'all', ...win }) });
  const skuQ = useQuery({ queryKey: ['shopify-skumap'], queryFn: () => getShopifySkuMap() });
  const stockQ = useQuery({ queryKey: ['shopify-stock-preview'], queryFn: () => getShopifyStockPreview() });
  const profitQ = useQuery({ queryKey: ['online-profitability', win], queryFn: () => getOnlineProfitability(win) });
  const shipQ = useQuery({ queryKey: ['shipping-rows', range?.from ?? null, range?.to ?? null], queryFn: () => listShippingRows(win) });

  const orders = ordersQ.data ?? [];
  const profitRows = profitQ.data?.rows ?? [];

  const orderCounts = useMemo(() => {
    const c: Record<string, number> = { received: 0, processing: 0, done: 0, ignored: 0, held: 0, cancelled: 0 };
    for (const o of orders) c[bucketOf(o)]++;
    return c;
  }, [orders]);

  // Order value (Shopify order total, incl GST) per bucket — shown under each count tile.
  const orderValues = useMemo(() => {
    const v: Record<string, number> = { all: 0, received: 0, processing: 0, done: 0, ignored: 0, held: 0, cancelled: 0 };
    for (const o of orders) {
      const amt = Number(o.totalPrice) || 0;
      v[bucketOf(o)] += amt;
      v.all += amt;
    }
    return v;
  }, [orders]);

  const payCounts = useMemo(() => {
    const c = { paid: 0, partial: 0, pending: 0, other: 0 };
    for (const o of orders) c[PAY_BUCKET[o.financialStatus ?? ''] ?? 'other']++;
    return c;
  }, [orders]);

  const shipCounts = useMemo(() => {
    const c = { notBooked: 0, booked: 0, transit: 0, delivered: 0, ndr: 0, rto: 0, void: 0 };
    for (const r of shipQ.data ?? []) {
      if (!r.shipment) c.notBooked++;
      else c[SHIP_STAGE[r.shipment.status] ?? 'booked']++;
    }
    return c;
  }, [shipQ.data]);

  // Shipments on the return path (RTO) or one failed attempt away from it (NDR), with the order value at stake.
  const rto = useMemo(() => {
    const valueByOrder = new Map(orders.map((o) => [o.orderName, Number(o.totalPrice) || 0]));
    const rows = (shipQ.data ?? []).flatMap((r) => {
      const stage = r.shipment ? rtoStage(r.shipment) : null;
      return stage && r.shipment ? [{ ...r, shipment: r.shipment, stage, value: r.orderValue ?? valueByOrder.get(r.orderName) ?? 0 }] : [];
    }).sort((a, b) => Date.parse(b.shipment.statusAt ?? '') - Date.parse(a.shipment.statusAt ?? '') || 0);
    const count = (k: RtoStage) => rows.filter((x) => x.stage === k).length;
    const value = (k: RtoStage) => rows.filter((x) => x.stage === k).reduce((a, x) => a + x.value, 0);
    // RTO rate is over live bookings — cancelled and failed bookings never shipped.
    const shipped = (shipQ.data ?? []).filter((r) => r.shipment?.awbNo && !['Cancelled', 'Failed'].includes(r.shipment.status)).length;
    const total = count('returning') + count('returned');
    return {
      rows, shipped, total,
      returning: count('returning'), returned: count('returned'), ndr: count('ndr'),
      rtoValue: value('returning') + value('returned'), ndrValue: value('ndr'),
    };
  }, [shipQ.data, orders]);

  // One row per bucket of the period (see trendBuckets): orders placed, and ex-GST value split into costs + gross profit.
  const trend = useMemo(() => trendBuckets(range), [range]);
  const daily = useMemo(() => {
    const rows = trend.rows.map((r) => ({ ...r }));
    const byKey = new Map(rows.map((r) => [r.key, r]));
    for (const o of orders) {
      const r = o.shopifyCreatedAt ? byKey.get(trend.keyOf(new Date(o.shopifyCreatedAt))) : undefined;
      if (r) r.orders++;
    }
    for (const p of profitRows) {
      const r = p.orderDate ? byKey.get(trend.keyOf(new Date(p.orderDate))) : undefined;
      if (r) { r.costs += p.valueWithoutGst - p.grossProfit; r.profit += p.grossProfit; }
    }
    for (const r of rows) { r.costs = Math.round(r.costs * 100) / 100; r.profit = Math.round(r.profit * 100) / 100; }
    return rows;
  }, [orders, profitRows, trend]);
  const when = range ? range.label : `Last ${TREND_DAYS} days`;
  const inWhen = range ? `in ${range.label}` : `in the last ${TREND_DAYS} days`;

  const windowOrders = daily.reduce((a, r) => a + r.orders, 0);
  const windowCosts = daily.reduce((a, r) => a + r.costs, 0);
  const windowProfit = daily.reduce((a, r) => a + r.profit, 0);
  const peakIdx = daily.reduce((best, r, i) => (r.orders > daily[best].orders ? i : best), 0);

  const cities = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of orders) {
      const c = (o.shipCity ?? '').trim().toLowerCase();
      if (c) m.set(c, (m.get(c) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([name, count]) => ({ name: name.replace(/\b\w/g, (ch) => ch.toUpperCase()), count }));
  }, [orders]);

  const sku = skuQ.data?.summary;
  const totals = profitQ.data?.totals;
  const margin = totals && totals.valueWithoutGst > 0 ? (totals.grossProfit / totals.valueWithoutGst) * 100 : null;
  // Orders whose item costing is missing or partial — their profit is overstated.
  const uncosted = profitRows.filter((r) => r.unmappedLines > 0 || r.totalCosting === 0).length;
  const liveSegs = ORDER_SEG.map((s) => ({ label: s.label, color: s.color, value: orderCounts[s.key] })).filter((s) => s.value > 0);
  const hovered = hoverSeg == null ? undefined : liveSegs[hoverSeg];

  return (
    <div className="space-y-4">
      <SectionHeader title="Ecommerce - Dashboard" subtitle={range ? range.label : undefined} actions={<PeriodPicker {...period.picker} units={REPORT_UNITS} />} />

      {/* Order KPIs — colours match the Order Status chart below */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Total Orders" value={orders.length} amount={orderValues.all} accent="#6366f1" Icon={ShoppingBag} loading={ordersQ.isLoading} />
        <Kpi label="Awaiting" value={orderCounts.received} amount={orderValues.received} accent={C.blue} Icon={ArrowRightCircle} loading={ordersQ.isLoading} />
        <Kpi label="Processing" value={orderCounts.processing} amount={orderValues.processing} accent={C.accent} Icon={RefreshCw} loading={ordersQ.isLoading} />
        <Kpi label="Done" value={orderCounts.done} amount={orderValues.done} accent={C.green} Icon={CheckCircle2} loading={ordersQ.isLoading} />
        <Kpi label="On Hold" value={orderCounts.held} amount={orderValues.held} accent={C.violet} Icon={Timer} loading={ordersQ.isLoading} />
        <Kpi label="Cancelled" value={orderCounts.cancelled} amount={orderValues.cancelled} accent={C.red} Icon={Ban} loading={ordersQ.isLoading} />
      </div>

      {/* Money KPIs — all from the profitability report so the row is internally consistent; RTO closes the row
          so both rows share the same six columns. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Revenue" sub="incl GST" value={totals ? compact(totals.valueWithGst) : '—'} hint={totals && money(totals.valueWithGst)} accent={C.accent} Icon={Wallet} loading={profitQ.isLoading} />
        <Kpi label="Order Value" sub="ex-GST" value={totals ? compact(totals.valueWithoutGst) : '—'} hint={totals && money(totals.valueWithoutGst)} accent="#0ea5e9" Icon={Wallet} loading={profitQ.isLoading} />
        <Kpi label="Product Costing" sub="item master" value={totals ? compact(totals.totalCosting) : '—'} hint={totals && money(totals.totalCosting)} accent={C.amber} Icon={Boxes} loading={profitQ.isLoading} />
        <Kpi label="Gross Profit" sub="ex-GST − costs" value={totals ? compact(totals.grossProfit) : '—'} hint={totals && money(totals.grossProfit)} accent={C.green} Icon={TrendingUp} loading={profitQ.isLoading} />
        <Kpi label="Margin" sub="profit ÷ ex-GST" value={margin == null ? '—' : `${margin.toFixed(1)}%`} accent="#ec4899" Icon={Percent} loading={profitQ.isLoading} />
        <Kpi
          label="RTO" value={rto.total} amount={rto.rtoValue} accent="#e11d48" Icon={RotateCcw} loading={shipQ.isLoading || ordersQ.isLoading}
          hint={`${rto.returning} returning · ${rto.returned} returned · ${pct(rto.total, rto.shipped)} of ${rto.shipped} shipped — ${money(rto.rtoValue)} incl GST`}
        />
      </div>

      {/* Order volume trend + status mix */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="md:col-span-2" title={STEP_TEXT[trend.step].orders} sub={`${when} · ${windowOrders} orders placed`} icon={ShoppingBag}>
          {ordersQ.isLoading ? <ChartSkeleton /> : windowOrders === 0 ? <Empty text={`No orders placed ${inWhen}.`} /> : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={daily} margin={{ top: 20, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="ecomOrdersGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.accent} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={C.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={28} />
                <YAxis allowDecimals={false} width={30} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} cursor={{ stroke: 'var(--text-muted)', strokeWidth: 1 }} />
                <Area
                  type="monotone" dataKey="orders" name="Orders" stroke={C.accent} strokeWidth={2.5} fill="url(#ecomOrdersGrad)" isAnimationActive={false}
                  activeDot={{ r: 4, stroke: SURFACE, strokeWidth: 2 }}
                  // Direct-label only the busiest day; the axis and tooltip carry the rest.
                  dot={(p: { cx?: number; cy?: number; index?: number }) => (p.index === peakIdx && p.cx != null && p.cy != null ? (
                    <g key={p.index}>
                      <circle cx={p.cx} cy={p.cy} r={4} fill={C.accent} stroke={SURFACE} strokeWidth={2} />
                      <text x={p.cx} y={p.cy - 9} textAnchor="middle" fontSize={10} fontWeight={700} fill="var(--text-primary)">{daily[peakIdx].orders}</text>
                    </g>
                  ) : <g key={p.index} />)}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Order Status" sub={range ? `Orders placed ${range.label}` : 'All pulled orders'} icon={CheckCircle2} to="/sales/online-orders" linkLabel="Open">
          {ordersQ.isLoading ? <ChartSkeleton /> : orders.length === 0 ? <Empty text={range ? `No orders placed in ${range.label}.` : 'No online orders pulled yet.'} /> : (
            <>
              <div className="relative mx-auto h-36 w-36" onMouseLeave={() => setHoverSeg(null)}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={liveSegs} dataKey="value" nameKey="label" innerRadius={46} outerRadius={66} paddingAngle={2} stroke="none"
                      startAngle={90} endAngle={-270} isAnimationActive={false}
                      onMouseEnter={(_, i) => setHoverSeg(i)} onMouseLeave={() => setHoverSeg(null)}
                    >
                      {liveSegs.map((s, i) => <Cell key={s.label} fill={s.color} fillOpacity={hoverSeg == null || hoverSeg === i ? 1 : 0.35} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                {/* Centre readout: the total, or the hovered segment — no floating tooltip to collide with it. */}
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-extrabold text-[var(--text-primary)]">{hovered ? hovered.value : orders.length}</span>
                  <span className="max-w-[80px] truncate text-[9px] font-bold uppercase tracking-widest text-[var(--text-muted)]">{hovered ? hovered.label : 'Orders'}</span>
                  {hovered && <span className="text-[10px] font-semibold text-[var(--text-secondary)]">{pct(hovered.value, orders.length)}</span>}
                </div>
              </div>
              <Legend segments={liveSegs} total={orders.length} />
            </>
          )}
        </Card>
      </div>

      {/* Daily value split + where orders ship */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="md:col-span-2" title={STEP_TEXT[trend.step].value} sub={`${when} · each bar splits into costs and gross profit`} icon={TrendingUp} to="/sales/online-profitability" linkLabel="Open Profitability">
          {profitQ.isLoading ? <ChartSkeleton /> : windowCosts + windowProfit === 0 ? <Empty text={`No order value ${inWhen}.`} /> : (
            <>
              <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--text-secondary)]">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.accent }} />Costs <strong className="text-[var(--text-primary)]">{compact(windowCosts)}</strong></span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.green }} />Gross profit <strong className="text-[var(--text-primary)]">{compact(windowProfit)}</strong></span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={daily} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={28} />
                  <YAxis width={48} tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={axisMoney} />
                  <Tooltip content={<Tip fmt={money} sum />} cursor={{ fill: 'var(--text-muted)', fillOpacity: 0.1 }} />
                  <Bar dataKey="costs" name="Costs" stackId="v" fill={C.accent} maxBarSize={16} stroke={SURFACE} strokeWidth={1} isAnimationActive={false} />
                  <Bar dataKey="profit" name="Gross profit" stackId="v" fill={C.green} maxBarSize={16} radius={[4, 4, 0, 0]} stroke={SURFACE} strokeWidth={1} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
              {uncosted > 0 && (
                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-[var(--text-muted)]">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0 text-warning" />
                  {uncosted} of {profitRows.length} orders have missing or partial item costing, so profit and margin read higher than they are.
                </p>
              )}
            </>
          )}
        </Card>

        <Card title="Top cities" sub={range ? `By orders placed ${range.label}` : 'By orders placed'} icon={MapPin}>
          {ordersQ.isLoading ? <ChartSkeleton /> : cities.length === 0 ? <Empty text="No shipping cities yet." /> : (
            <div className="space-y-2">
              {cities.map((c) => (
                <div key={c.name} title={`${c.name}: ${c.count} orders`}>
                  <div className="flex items-baseline justify-between gap-2 text-xs">
                    <span className="truncate text-[var(--text-secondary)]">{c.name}</span>
                    <strong className="text-[var(--text-primary)]">{c.count}</strong>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full" style={{ background: 'rgba(255,140,0,0.14)' }}>
                    <div className="h-full rounded-full" style={{ width: `${(c.count / cities[0].count) * 100}%`, background: C.accent }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Payment, shipping and catalogue health */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card title="Payment status" sub={range ? `Orders placed ${range.label}` : 'As reported by Shopify'} icon={Wallet}>
          <SegBar segments={PAY_SEG.map((s) => ({ label: s.label, color: s.color, value: payCounts[s.key] }))} total={orders.length} empty="No online orders pulled yet." />
        </Card>

        <Card title="Shipping" sub={range ? `Dispatched ${range.label} via Delhivery` : 'Dispatches via Delhivery'} icon={Truck} to="/sales/shipping" linkLabel="Open">
          <SegBar segments={SHIP_SEG.map((s) => ({ label: s.label, color: s.color, value: shipCounts[s.key] }))} total={shipQ.data?.length ?? 0} empty="No online dispatches to ship yet." />
        </Card>

        <Card title="Catalogue sync" sub="Shopify SKUs mapped to ERP" icon={Link2} to="/admin/configurations/shopify-mapping" linkLabel="Open">
          {/* Same look as Payment status / Shipping: a split bar of the catalogue, then follow-ups. */}
          <SegBar
            segments={[
              { label: 'Mapped to ERP', color: C.green, value: sku?.mapped ?? 0 },
              { label: 'Unmapped SKUs', color: C.amber, value: sku?.unmapped ?? 0 },
            ]}
            total={sku?.total ?? 0}
            empty="No Shopify SKUs pulled yet."
          />
          {/* Follow-ups: icon · what · how many · where to act — one grid so every column lines up. */}
          <div className="mt-3 border-t border-[var(--border-color)] pt-2.5 text-xs">
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">To do</div>
            <div className="grid grid-cols-[16px_1fr_auto_52px] items-center gap-x-2 gap-y-2">
              <Row Icon={Link2} label="SKUs to map" value={sku?.unmapped ?? 0} action="Map" to="/admin/configurations/shopify-mapping" />
              <Row Icon={Upload} label="Stock to push" value={stockQ.data?.toPush ?? 0} action="Push" to="/admin/configurations/shopify-stock" />
              <Row Icon={EyeOff} label="Untracked in Shopify" value={sku?.untracked ?? 0} hint="Shopify isn't tracking stock for these SKUs — turn on tracking in Shopify" />
            </div>
          </div>
        </Card>
      </div>

      {/* Returns to origin — what's coming back, what's back, and what's at risk */}
      <Card title="Returns to origin (RTO)" sub={`Delhivery shipments returning, returned, or with a failed delivery attempt${range ? ` · dispatched ${range.label}` : ''}`} icon={RotateCcw} to="/sales/shipping" linkLabel="Open Shipping">
        {shipQ.isLoading ? <ChartSkeleton /> : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <Kpi label="RTO Rate" sub={`${rto.total} of ${rto.shipped} shipped`} value={pct(rto.total, rto.shipped)} accent="#e11d48" Icon={Percent} />
              <Kpi label="Returning" sub="on the way back" value={rto.returning} accent={RTO_STAGE.returning.color} Icon={Truck} />
              <Kpi label="Returned" sub="back at origin" value={rto.returned} accent={RTO_STAGE.returned.color} Icon={Undo2} />
              <Kpi label="RTO Value" sub="order value, incl GST" value={compact(rto.rtoValue)} hint={money(rto.rtoValue)} accent="#e11d48" Icon={Wallet} />
              <Kpi className="col-span-2 md:col-span-1" label="NDR at Risk" sub={rto.ndr ? `${compact(rto.ndrValue)} incl GST` : 'no failed attempts'} value={rto.ndr} hint={money(rto.ndrValue)} accent={RTO_STAGE.ndr.color} Icon={AlertTriangle} />
            </div>
            {rto.rows.length === 0 ? (
              <Empty text="No shipments returning to origin or with a failed delivery attempt." />
            ) : (
              <div className="mt-4 divide-y divide-[var(--border-color)] border-t border-[var(--border-color)]">
                {rto.rows.map((r) => (
                  // Phone: order + value on top, stage and AWB below. md+: one line — order, stage, AWB, value.
                  <div key={r.outwardGrnId} className="grid grid-cols-[1fr_auto] items-start gap-x-4 gap-y-1 py-2.5 text-xs md:grid-cols-[1fr_1.3fr_auto_80px] md:items-center">
                    <div className="min-w-0">
                      <div className="font-extrabold text-accent">{r.orderName ?? r.enqNo ?? r.grnNo}</div>
                      <div className="truncate text-[var(--text-muted)]">{[r.consignee, r.city].filter(Boolean).join(' · ') || '—'}</div>
                    </div>
                    <div className="text-right font-bold tabular-nums text-[var(--text-primary)] md:order-last" title={money(r.value)}>{r.value ? compact(r.value) : '—'}</div>
                    <div className="col-span-2 min-w-0 md:col-span-1">
                      <span className="inline-flex items-center gap-1.5 font-semibold text-[var(--text-primary)]">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: RTO_STAGE[r.stage].color }} />
                        {RTO_STAGE[r.stage].label}
                      </span>
                      <div className="break-words text-[var(--text-muted)]">
                        {[r.shipment.statusAt && new Date(r.shipment.statusAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }), r.shipment.statusLocation].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </div>
                    <div className="col-span-2 font-mono text-[11px] text-[var(--text-secondary)] md:col-span-1"><span className="font-sans text-[var(--text-muted)] md:hidden">AWB </span>{r.shipment.awbNo}</div>
                  </div>
                ))}
              </div>
            )}
            {rto.returned > 0 && (
              <p className="mt-3 flex items-start gap-1.5 text-[11px] text-[var(--text-muted)]">
                <AlertTriangle size={12} className="mt-0.5 shrink-0 text-warning" />
                Returned parcels aren’t restocked automatically — book a Sales Return once the goods are checked in.
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, sub, value, amount, hint, accent, Icon, loading, className = '' }: {
  label: string; sub?: string; value: string | number;
  /** Rupee value of the orders counted in this tile (incl GST) — shown under the count. */
  amount?: number;
  hint?: string | false | null; accent: string; Icon?: LucideIcon; loading?: boolean; className?: string;
}) {
  return (
    <div className={`relative overflow-hidden rounded-xl p-4 text-white shadow-sm transition-transform hover:-translate-y-0.5 ${className}`} style={{ background: accent, textShadow: KPI_TEXT_SHADOW }} title={hint || (amount != null ? `${money(amount)} order value, incl GST` : undefined)}>
      {Icon && <Icon className="absolute -bottom-3 -right-3 h-20 w-20" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />}
      <div className="relative">
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/90">{label}</span>
        <div className="mt-1.5 text-2xl font-extrabold">
          {loading ? <span className="inline-block h-6 w-16 animate-pulse rounded bg-white/30" /> : value}
        </div>
        {sub && <div className="text-[10px] font-semibold text-white/85">{sub}</div>}
        {amount != null && !loading && (
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-base font-extrabold leading-none">{compact(amount)}</span>
            <span className="text-[10px] font-semibold text-white/85">incl GST</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ title, sub, icon: Icon, to, linkLabel, className = '', children }: {
  title: string; sub?: string; icon: LucideIcon; to?: string; linkLabel?: string; className?: string; children: React.ReactNode;
}) {
  return (
    <div className={`rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${className}`}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-extrabold text-[var(--text-primary)]"><Icon size={15} className="shrink-0 text-accent" /> {title}</div>
          {sub && <div className="mt-0.5 text-[10px] text-[var(--text-muted)]">{sub}</div>}
        </div>
        {to && <Link to={to} className="shrink-0 text-[11px] font-semibold text-accent hover:underline">{linkLabel} →</Link>}
      </div>
      {children}
    </div>
  );
}

/** Shared chart tooltip — the value leads, the series name follows. */
function Tip({ active, payload, label, fmt = String, sum }: {
  active?: boolean;
  payload?: { name?: string; value?: number; color?: string }[];
  label?: string; fmt?: (n: number) => string; sum?: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 text-xs shadow-2xl">
      {label && <div className="mb-1.5 font-bold text-[var(--text-primary)]">{label}</div>}
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 py-0.5">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <strong className="text-[var(--text-primary)]">{fmt(p.value ?? 0)}</strong>
          <span className="text-[var(--text-muted)]">{p.name}</span>
        </div>
      ))}
      {sum && (
        <div className="mt-1 border-t border-[var(--border-color)] pt-1 text-[var(--text-muted)]">
          <strong className="text-[var(--text-primary)]">{fmt(payload.reduce((a, p) => a + (p.value ?? 0), 0))}</strong> total
        </div>
      )}
    </div>
  );
}

function Legend({ segments, total }: { segments: { label: string; color: string; value: number }[]; total: number }) {
  return (
    <div className="mt-3 space-y-1.5">
      {segments.map((s) => (
        <div key={s.label} className="flex items-center justify-between gap-2 text-xs">
          <span className="flex min-w-0 items-center gap-1.5 text-[var(--text-secondary)]"><span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} /><span className="truncate">{s.label}</span></span>
          <span className="shrink-0 text-[var(--text-muted)]"><strong className="text-[var(--text-primary)]">{s.value}</strong> · {pct(s.value, total)}</span>
        </div>
      ))}
    </div>
  );
}

function SegBar({ segments, total, empty }: { segments: { label: string; color: string; value: number }[]; total: number; empty: string }) {
  if (total === 0) return <Empty text={empty} />;
  return (
    <div>
      <div className="flex h-3 w-full gap-[2px] overflow-hidden rounded-full">
        {segments.filter((s) => s.value > 0).map((s) => (
          <div key={s.label} className="transition-opacity hover:opacity-80" style={{ width: `${(s.value / total) * 100}%`, background: s.color }} title={`${s.label}: ${s.value} (${pct(s.value, total)})`} />
        ))}
      </div>
      <Legend segments={segments} total={total} />
    </div>
  );
}

/** One follow-up line in a 4-column grid: icon · label · count · action. Amber when there's something to do. */
function Row({ Icon, label, value, action, to, hint }: { Icon: LucideIcon; label: string; value: number; action?: string; to?: string; hint?: string }) {
  const pending = value > 0;
  return (
    <>
      <Icon size={13} className={pending ? 'text-warning' : 'text-[var(--text-muted)]'} />
      <span className="truncate text-[var(--text-secondary)]" title={hint}>{label}</span>
      <strong className={`text-right tabular-nums ${pending ? 'text-warning' : 'text-[var(--text-primary)]'}`}>{value}</strong>
      {action && to && pending
        ? <Link to={to} className="whitespace-nowrap text-right font-semibold text-accent hover:underline">{action} →</Link>
        : <span className="text-right text-[var(--text-muted)]">{pending ? '' : '✓'}</span>}
    </>
  );
}

function ChartSkeleton() {
  return <div className="h-[220px] animate-pulse rounded-lg" style={{ background: 'var(--border-color)' }} />;
}

function Empty({ text }: { text: string }) {
  return <div className="py-8 text-center text-xs text-[var(--text-muted)]">{text}</div>;
}
